import type { Level } from '../../sim/level';
import type { DrillId, DrillResult } from '../drill';
import type { FrameState } from '../frame';
import { crashSituation, drillSituation, landingSituation, Situation, slowSector } from './situation';

/** Average speed (m/s) a sector is measured against; slower than 1/SLOW_FACTOR of it is a slow sector. */
const SECTOR_PACE = { track: 8, enduro: 6 } as const;
const SLOW_FACTOR = 1.6;

/**
 * Watches a ride for the moments a how-to demo helps: a fall (by cause, at the feature it happened on), a cased or
 * overshot jump that did not end in a fall, a drill finished under 3 stars, a slow sector. Fed once per physics step;
 * allocation-free except on the rare step something happens.
 */
export class FailureWatcher {
  private wasFallen = false;
  private landedAt: number | null = null;
  private s = 0;
  private sectorStart = NaN;
  private sectorClean = true;

  constructor(readonly level: Level) {}

  /** A respawn or a restart: the fall is over, the sector no longer counts. */
  reset(): void { this.wasFallen = false; this.landedAt = null; this.sectorClean = false; }

  step(f: FrameState): Situation | null {
    const line = this.level.line;
    if (!line) return null;
    const b = f.bike, t = b.telemetry;
    const fallen = t.fallen;
    if (!fallen && !t.airborne) this.s = line.closest(b.position.x, b.position.z, this.s, 20).s;
    let out: Situation | null = null;
    if (t.landing) {
      this.landedAt = this.s;
      if (t.landing.verdict === 'hard') out = landingSituation(this.level, this.s);
    }
    if (fallen && !this.wasFallen) {
      const braking = Math.max(f.input.brake, f.input.frontBrake ?? 0) > 0.2;
      out = crashSituation(this.level, this.s, t, braking, this.landedAt);
      this.sectorClean = false;
    }
    this.wasFallen = fallen;
    return out;
  }

  /** A checkpoint passed at `time`: a slow sector since the previous one is reported (not if it had a fall or respawn). */
  checkpoint(index: number, time: number): Situation | null {
    const cps = this.level.checkpoints, line = this.level.line;
    const prevTime = this.sectorStart, clean = this.sectorClean;
    this.sectorStart = time;
    this.sectorClean = true;
    if (!line || !clean || !Number.isFinite(prevTime) || cps.length < 2) return null;
    const prev = cps[(index - 1 + cps.length) % cps.length]!;
    let len = cps[index]!.s - prev.s;
    if (len <= 0) len += line.length;
    const pace = this.level.def.id === 'enduro' ? SECTOR_PACE.enduro : SECTOR_PACE.track;
    return slowSector(this.level, index, time - prevTime, len / pace, SLOW_FACTOR);
  }

  static drill(id: DrillId, r: DrillResult): Situation | null { return drillSituation(id, r); }
}
